import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { Search, X, ChevronLeft, ChevronRight, ExternalLink, ImageIcon } from 'lucide-react';
import axios from 'axios';
import { API } from '../../config';

/**
 * Reusable image picker dialog backed by Openverse (Creative Commons, no API key).
 * Props:
 *   open          – boolean
 *   onClose       – () => void
 *   onSelect      – (url: string) => void   ← called with full-res image URL
 *   initialQuery  – string  (pre-filled search, e.g. "Dubai airport")
 */
export default function PhotoPickerDialog({ open, onClose, onSelect, initialQuery = '' }) {
  const [query, setQuery] = useState('');
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const doSearch = useCallback(async (q, p = 1) => {
    if (!q.trim()) return;
    setLoading(true);
    setImages([]);
    try {
      const res = await axios.get(`${API}/admin/image-search`, {
        params: { query: q, page: p },
        withCredentials: true,
      });
      setImages(res.data.images || []);
      setTotal(res.data.total || 0);
    } catch {
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // When dialog opens, run the initial query
  useEffect(() => {
    if (open && initialQuery) {
      setQuery(initialQuery);
      setPage(1);
      doSearch(initialQuery, 1);
    }
  }, [open, initialQuery, doSearch]);

  const handleSearch = () => {
    setPage(1);
    doSearch(query, 1);
  };

  const handlePage = (newPage) => {
    setPage(newPage);
    doSearch(query, newPage);
  };

  const handleSelect = (url) => {
    onSelect(url);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[88vh] flex flex-col" data-testid="photo-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-[#FF4500]" />
            Browse Photos
          </DialogTitle>
          <DialogDescription>
            Free Creative Commons images via Openverse. Click any photo to use it.
          </DialogDescription>
        </DialogHeader>

        {/* Search bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search photos… e.g. Dubai airport, flight, travel"
              className="pl-8 pr-8 h-9"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              data-testid="photo-search-input"
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setImages([]); }}
                className="absolute right-2.5 top-2.5 text-muted-foreground hover:text-foreground"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
          <Button onClick={handleSearch} size="sm" className="fly-btn-primary shrink-0" data-testid="photo-search-btn">
            Search
          </Button>
        </div>

        {/* Image grid */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
              {Array(15).fill(0).map((_, i) => (
                <Skeleton key={i} className="aspect-[4/3] rounded-lg" />
              ))}
            </div>
          ) : images.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <ImageIcon className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">
                {query ? 'No images found — try a different search term' : 'Enter a search term to browse photos'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-2">
              {images.map((img, idx) => (
                <button
                  key={`${img.url}-${idx}`}
                  onClick={() => handleSelect(img.url)}
                  className="group relative rounded-lg overflow-hidden aspect-[4/3] hover:ring-2 hover:ring-[#FF4500] hover:ring-offset-1 transition-all"
                  title={img.title}
                  data-testid={`photo-option-${idx}`}
                >
                  <img
                    src={img.thumbnail}
                    alt={img.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                    loading="lazy"
                    onError={e => { e.target.parentElement.style.display = 'none'; }}
                  />
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-1.5">
                    <p className="text-[9px] text-white line-clamp-2 leading-tight">{img.title}</p>
                  </div>
                  {/* CC license badge */}
                  {img.license && (
                    <span className="absolute top-1 right-1 text-[8px] bg-black/50 text-white px-1 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity uppercase">
                      {img.license}
                    </span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer: pagination + source info */}
        {images.length > 0 && (
          <div className="flex items-center justify-between pt-3 border-t border-border mt-2">
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              ~{total.toLocaleString()} results ·{' '}
              <a href="https://openverse.org" target="_blank" rel="noopener noreferrer" className="hover:underline inline-flex items-center gap-0.5">
                Openverse <ExternalLink className="w-2.5 h-2.5" />
              </a>
              {' '}· Creative Commons
            </p>
            <div className="flex items-center gap-1.5">
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => handlePage(page - 1)} disabled={page <= 1 || loading}>
                <ChevronLeft className="w-3.5 h-3.5" />
              </Button>
              <span className="text-xs text-muted-foreground">Page {page}</span>
              <Button size="sm" variant="outline" className="h-7 w-7 p-0" onClick={() => handlePage(page + 1)} disabled={images.length < 15 || loading}>
                <ChevronRight className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
